import {Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {select, Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import * as catalogSelectors from '../../../../catalog/catalog.selectors';
import {selectFailedAddons} from '../../../../shopping-cart/cart.selectors';
import {selectContextData} from 'src/app/common/context-data/context-data.selectors';
import {AddressFormGroup} from 'src/app/data/model';
import {
  addOriginalAddress,
  createAddress,
  selectOriginalAddress,
} from 'src/app/tax-and-shipping/address.actions';

interface AddOnFooterState {
  disableNextButton: boolean;
  displayRegisterAndCompleteButtons: boolean;
}

export interface AddOnFooterViewModel {
  disableNextButton: boolean;
  continueLink: string | undefined;
  isFinalStep: boolean;
  showContinueToCheckoutButton: boolean;
  displayRegisterAndCompleteButtons: boolean;
  motionRegistrantAddress: AddressFormGroup | undefined;
}

@Injectable()
export class AddOnFooterStore extends ComponentStore<AddOnFooterState> {
  readonly disableNextButton$: Observable<boolean> = this.select(
    state => state.disableNextButton,
  );

  readonly displayRegisterAndCompleteButtons$: Observable<boolean> =
    this.select(state => state.displayRegisterAndCompleteButtons);

  readonly nextRouteId$: Observable<string | undefined> = this.select(
    this.store.pipe(select(catalogSelectors.selectNextRouteFragment)),
    nextRouteFragment => nextRouteFragment,
  );

  readonly failedAddonsCount$: Observable<number | undefined> = this.select(
    this.store.pipe(select(selectFailedAddons)),
    failedAddons => failedAddons.length,
  );

  readonly showRegisterAnotherPersonButton$: Observable<boolean> = this.select(
    this.store.pipe(select(catalogSelectors.selectIsFinalCatalogPage)),
    this.displayRegisterAndCompleteButtons$,
    (isFinalCatalogPage: boolean, displayRegisterAndComplete: boolean) =>
      isFinalCatalogPage || displayRegisterAndComplete,
  );

  readonly continueLink$: Observable<string | undefined> = this.select(
    this.nextRouteId$,
    this.disableNextButton$,
    this.failedAddonsCount$,
    (
      nextRouteId: string | undefined,
      disableNextButton: boolean,
      failedAddonsCount,
    ) => {
      const nextIsAddressPage = nextRouteId
        ? nextRouteId.includes('address')
        : false;
      if (nextIsAddressPage && failedAddonsCount && failedAddonsCount > 0) {
        return 'commerce/failed-add-ons';
      }
      return disableNextButton ? undefined : nextRouteId;
    },
  );

  readonly showContinueToCheckoutButton$: Observable<boolean> = this.select(
    this.displayRegisterAndCompleteButtons$,
    displayRegisterAndComplete => displayRegisterAndComplete,
  );

  readonly motionRegistrantAddress$: Observable<AddressFormGroup | undefined> =
    this.select(this.store.pipe(select(selectContextData)), contextData => {
      if (contextData?.signups[0].program?.system === 'Motion') {
        const registrant = contextData.registrant;
        return {
          ...registrant.address!,
          address2: contextData?.registrant.address!.address2 ?? null,
          firstName: registrant.firstName!,
          lastName: registrant.lastName!,
        };
      } else {
        return undefined;
      }
    });

  readonly vm$: Observable<AddOnFooterViewModel> = this.select(
    this.disableNextButton$,
    this.continueLink$,
    this.showRegisterAnotherPersonButton$,
    this.showContinueToCheckoutButton$,
    this.displayRegisterAndCompleteButtons$,
    this.motionRegistrantAddress$,
    (
      disableNextButton: boolean,
      continueLink: string | undefined,
      isFinalStep: boolean,
      showContinueToCheckoutButton: boolean,
      displayRegisterAndCompleteButtons: boolean,
      motionRegistrantAddress,
    ): AddOnFooterViewModel => ({
      disableNextButton,
      continueLink,
      isFinalStep,
      showContinueToCheckoutButton,
      displayRegisterAndCompleteButtons,
      motionRegistrantAddress,
    }),
  );

  readonly setDisableNextButton = this.updater(
    (state, value: boolean): AddOnFooterState => ({
      ...state,
      disableNextButton: value,
    }),
  );

  /**
   * Setting this to true will force the footer to display the register and complete buttons only.
   */
  readonly setDisplayRegisterAndCompleteButtons = this.updater(
    (state, value: boolean): AddOnFooterState => ({
      ...state,
      displayRegisterAndCompleteButtons: value,
    }),
  );

  readonly setMotionAddress = this.updater(
    (state, address: AddressFormGroup): AddOnFooterState => {
      this.store.dispatch(
        selectOriginalAddress({
          originalSelected: true,
        }),
      );
      this.store.dispatch(
        addOriginalAddress({
          address,
        }),
      );
      this.store.dispatch(createAddress());
      return state;
    },
  );

  constructor(private store: Store) {
    super({
      disableNextButton: false,
      displayRegisterAndCompleteButtons: false,
    });
  }
}
